import React from 'react';
import constants from '../../../../constants/constants';
import {
  refillRequestDeliveryTypes,
  refillRequestPageTypes,
} from '../../../../config/app.config';
import {
  COPY_COMM_HISTORY_REFILL_HEADER_TOOLTIP,
  SEND_MESSAGE_REFILL_HEADER_TOOLTIP,
} from '../../../../shared/components/table/contextualHelpTooltipContent';

const attachmentIcon = `${process.env.PUBLIC_URL}/img/icons/file-attachment-icon.png`;
const refillStatusMobileExample = `${process.env.PUBLIC_URL}/img/refill-status-mobile-example.jpeg`;

const REFILL_STATUS_HEADER_TOOLTIP = (
  <div className="lp-info-tooltip__media-layout">
    <div className="lp-info-tooltip__media-text">
      <div className="lp-info-tooltip__section">
        <div className="lp-info-tooltip__heading-row">
          <span className="lp-info-tooltip__heading-text">
            Update Pet Parent about Refill Status
          </span>
        </div>
        <div className="lp-info-tooltip__body">
          <strong>
            Change the <em>Refill Status</em> in the dropdown menu to
            automatically update a Pet Parent on their phone with:
            <br />
            (i) a post in the Newsfeed in the Loyal Pet mobile app
            <br />
            (ii) a push notification.
          </strong>
        </div>
      </div>
      <div className="lp-info-tooltip__example-title">To the right:</div>
      <div className="lp-info-tooltip__example-caption">
        Example of what a Pet Parent sees <em>in the Newsfeed</em> in the Loyal
        Pet mobile app after the <em>Refill Status</em> is changed in the Web
        app.
      </div>
    </div>
    <img
      className="lp-info-tooltip__example-img"
      src={refillStatusMobileExample}
      alt="Example of refill status update in the mobile app"
    />
  </div>
);

const TOOLBAR_PLAIN_HEADER_KEYS = [
  'date',
  'parentFullName',
  'petName',
  'medication',
  'status',
  'doctorFullName',
  'deliveryType',
];

function withoutHeaderSortAndFilter(headers) {
  return headers.map((head) => {
    if (!TOOLBAR_PLAIN_HEADER_KEYS.includes(head.key)) {
      return head;
    }
    return {
      ...head,
      sortable: false,
      filterable: false,
    };
  });
}

function stripAllHeaderSortAndFilter(headers) {
  return headers.map((head) => ({
    ...head,
    sortable: false,
    filterable: false,
  }));
}

export function getRefillRequestsHeaders(
  type = refillRequestPageTypes.default,
) {
  const commonHeaders = [
    {
      key: 'date',
      name: 'Date',
      sortable: true,
      filterable: false,
      capitalize: true,
      className: 'date-column',
    },
    {
      key: 'time',
      name: 'Time',
      sortable: false,
      filterable: false,
      capitalize: true,
      className: 'time-column',
    },
    {
      key: 'copyHistory',
      name: 'Copy History',
      sortable: false,
      filterable: false,
      capitalize: false,
      className: 'comm-copy-history-column',
      headerTooltip: COPY_COMM_HISTORY_REFILL_HEADER_TOOLTIP,
      headerTooltipClassName: 'lp-info-tooltip',
    },
    {
      key: 'status',
      name: 'Refill Status',
      headerTooltip: REFILL_STATUS_HEADER_TOOLTIP,
      headerTooltipClassName: 'lp-info-tooltip lp-info-tooltip--with-image',
      sortable: false,
      filterable: true,
      capitalize: false,
      className: 'refill-request-status-column',
    },
    {
      key: 'message',
      name: 'Message',
      headerTooltip: SEND_MESSAGE_REFILL_HEADER_TOOLTIP,
      headerTooltipClassName: 'lp-info-tooltip',
      sortable: false,
      filterable: false,
      capitalize: false,
      className: 'refill-request-message-column',
    },
    {
      key: 'messageStatus',
      name: 'Reply',
      sortable: false,
      filterable: false,
      capitalize: false,
      className: 'refill-request-message-status-column',
    },
    {
      key: 'internalComments',
      name: 'Staff Notes',
      className: 'refill-request-staff-notes-column',
      sortable: false,
      filterable: false,
      capitalize: false,
    },
    {
      key: 'attachments',
      name: 'Attachments',
      imgSrc: attachmentIcon,
      imgAlt: 'File attachment icon',
      className: 'with-icon refill-request-attachment-column',
      sortable: false,
      filterable: false,
      capitalize: false,
    },
    {
      key: 'comments',
      name: 'Client Comments',
      sortable: false,
      filterable: false,
      capitalize: false,
      className: 'refill-request-client-comments-column',
    },
    {
      key: 'parentFullName',
      name: 'Parent Name',
      sortable: true,
      filterable: false,
      capitalize: true,
      link: (row) =>
        `/client-list/profile/${row.parentId}?tab=${constants.QUERY_TAB_PARAMS.parent}`,
      className: 'refill-request-parent-column',
    },
    {
      key: 'petName',
      name: 'Pet Name',
      sortable: true,
      filterable: false,
      capitalize: true,
      link: (row) =>
        `/client-list/profile/${row.parentId}?tab=${constants.QUERY_TAB_PARAMS.petInfo}&${constants.QUERY_PARAMS.pet}=${row.petId}`,
      className: 'refill-request-pet-column',
    },
    {
      key: 'doctorFullName',
      name: 'Doctor',
      sortable: false,
      filterable: true,
      filterValue: [],
      capitalize: true,
      className: 'refill-request-doctor-column',
    },
    {
      key: 'medication',
      name: 'Medication',
      sortable: true,
      filterable: false,
      capitalize: true,
      className: 'refill-request-medication-column',
    },
    {
      key: 'quantity',
      name: 'Quantity',
      sortable: false,
      filterable: false,
      capitalize: true,
      className: 'refill-request-quantity-column',
    },
    {
      key: 'dosage',
      name: 'Dosage',
      sortable: false,
      filterable: false,
      capitalize: true,
      className: 'refill-request-dosage-column',
    },
    {
      key: 'deliveryType',
      name: 'Delivery',
      sortable: false,
      filterable: true,
      filterValue: Object.entries(refillRequestDeliveryTypes).map(
        ([key, value]) => ({ [key]: value }),
      ),
      capitalize: true,
      className: 'refill-request-delivery-column',
    },
  ];

  const headersTypes = {
    [refillRequestPageTypes.default]: withoutHeaderSortAndFilter(commonHeaders),
    [refillRequestPageTypes.clientPage]: stripAllHeaderSortAndFilter(
      commonHeaders.filter(({ key }) => key !== 'parentFullName'),
    ),
  };

  return headersTypes[type];
}
